import { dayjs, toDateString } from '@koob/dates';
import {
  ExperienceStateEnum,
  ExtraScopeEnum,
  ExtraSourceEnum,
  ServiceApplicationDateKindEnum,
} from '@koob/enums';
import { isTransferTypology } from './effectiveEndPosition';

export const getExtraTitle = ({ extra, extrasGroup, translation }) => {
  const groupKey = `${extra.scope}-${extra.dayIndex}`;

  if (!extrasGroup.has(groupKey)) {
    extrasGroup.add(groupKey);
    return (
      <div className="text-sm uppercase font-semibold text-gray-500">
        {`${translation('extras.title')} - ${
          extra.scope === ExtraScopeEnum.DAY
            ? `${translation('extras.day')} ${extra.dayIndex + 1}`
            : ExtraScopeEnum.GLOBAL
        }`}
      </div>
    );
  }
  return null;
};

// The options a guest has to be assigned to, and the ones that get booked.
//
// 0 IS a price: an option the DMC offers for free is still an option the TO
// picked. Testing the price for truthiness conflated it with "no price at all",
// which dropped free options from the additional-services step and from the
// booking payload while the trip kept showing them.
//
// A service included in the experience carries no amount at all — it is part of
// what was already sold. A secondary option is covered by the guests assigned
// to the option it hangs from, so it takes no assignment of its own.
export const isAssignableExtra = (extra) =>
  extra?.price != null && !extra?.isIncluded && !extra?.parentExtraId;

// Whether an extra is one the basket synthesized from a program's included
// experiences, rather than an option the TO picked.
//
// Those entries are rebuilt from the programs on every basket refresh, so they
// must be dropped and regenerated, never carried over: carrying one and
// clearing its flag — which is what happens to a user's option, since a picked
// option is never an included service — turns a genuine included service into a
// priced option, and the stale entry then shadows the regenerated one.
//
// `source` identifies them, but entries written before that field existed carry
// only `isIncluded`. Reading the flag too is what makes this safe on them: the
// synthesis is the ONLY thing that ever sets it, so a flagged extra is always
// one an earlier pass produced.
export const isProgramIncludedExtra = (extra) =>
  extra?.source === ExtraSourceEnum.PROGRAM_INCLUDED ||
  extra?.isIncluded === true;

// KD-3749 — whether the status of a service lets it be proposed. Read on the
// service itself, so it holds before any date is searched, when no price can
// tell yet. A service listed without its status is not held back on that.
export const isExtraStateAvailable = (extra) =>
  !extra?.state || extra.state === ExperienceStateEnum.AVAILABLE;

// KD-3749 — whether a pricing period covers the dates the prices were asked
// for. The API only prices a service that is available and has a period on
// those dates, so a service left without any pricing is one that cannot be
// used: wrong status, or no period.
//
// Read on the grid as a whole, not on the party currently selected: a pax
// count the grid does not answer is an occurrence-level matter, and the service
// is still one the user can pick. 0 is a price, a free service is offered.
export const isExtraPricedForPeriod = (prices, extraId) =>
  !!prices
    ?.find((price) => price.id === extraId)
    ?.pricings?.some((pricing) => pricing?.price != null);

// KD-3749 — whether an additional service is one to propose. Prices are
// fetched asynchronously and stay undefined until the query resolves: an array,
// even an empty one, means the answer arrived. Until then nothing is known
// about the service, and it is not held back on that.
//
// A day service applies on one day of its program, and its pricing is read
// over the whole stay: the API lists apart the program days no period covers.
// `programDayIndex` is that day, 0-based, and is left out for a global service.
export const isExtraOfferedForPeriod = (prices, extraId, programDayIndex) => {
  if (!Array.isArray(prices)) {
    return true;
  }

  if (!isExtraPricedForPeriod(prices, extraId)) {
    return false;
  }

  return (
    programDayIndex == null ||
    !prices
      .find((price) => price.id === extraId)
      ?.unavailableDayIndexes?.includes(programDayIndex)
  );
};

// KD-3749 — the program day a service of `experience` is suggested on, 0-based,
// or undefined for a global one. A list that mixes both — the services
// recommended towards the next experience — has no single day to be read on:
// each service is checked on its own. Day configuration takes precedence over
// the global one, the way the card of the service resolves its scope.
export const getExtraProgramDayIndex = (experience, extraId) => {
  const index = (experience?.programs ?? []).findIndex((program) =>
    program?.suggestedExperiences?.some((extra) => extra.id === extraId),
  );

  return index >= 0 ? index : undefined;
};

// KD-3749 — the extras prices of an experience, as the answer they are.
//
// The API leaves `extras` out of a price when it priced none of the services
// attached to the experience — all of them on a status that cannot be used,
// typically. That is an answer, not a pending request: read as "not loaded
// yet", it kept every one of those services on display. So a price that
// arrived always yields a list, and only a price still missing yields nothing.
export const getResolvedExtrasPrices = (price) =>
  price ? (price.extras ?? []) : undefined;

// KD-3650 — whether a sub-option hangs from THIS occurrence of its parent
// service, now that the same service can be retained several times on a day.
//
// `parentExtraId` names an experience, not a line: with two occurrences of the
// parent it is true of both, and the sub-option would be read under each of
// them. `parentOccurrenceKey` names the occurrence it was actually ticked
// under, and it decides as soon as both sides carry a key.
//
// Either side missing one is a line saved before this ticket — the key is
// nullable and never backfilled — and nothing is left to tell the occurrences
// apart, so the service-level match stands, exactly as it did before.
export const isUnderParentOccurrence = (extra, parentExtra) =>
  extra?.parentOccurrenceKey == null ||
  parentExtra?.occurrenceKey == null ||
  extra.parentOccurrenceKey === parentExtra.occurrenceKey;

// The sub-options ticked under an option, on the same day.
export const getSecondaryExtras = (extras, parentExtra) =>
  (extras ?? []).filter(
    (extra) =>
      extra?.parentExtraId === parentExtra?.id &&
      extra?.dayIndex === parentExtra?.dayIndex &&
      isUnderParentOccurrence(extra, parentExtra) &&
      extra?.price != null &&
      !extra?.isIncluded,
  );

// KD-3650 — the product a selected extra stands for: everything it is filed
// under, occurrence aside.
//
// Two occurrences of the same service agree on every one of these fields, so
// this key is what a service synthesized from a program's included experiences
// is matched on — it carries no occurrence of its own, and still has to give
// way to an option the TO picked for the same product.
export const getExtraProductKey = (extra) =>
  `${extra?.id}-${extra?.experienceId}-${extra?.parentExtraId ?? ''}-${
    extra?.dayIndex
  }-${extra?.scope}`;

// The identity of a selected extra LINE: the product plus the occurrence it
// belongs to.
//
// Deduplicating on the product alone kept the first occurrence and dropped the
// rest, so a service retained twice reached the basket as a single line: the
// guest-assignment step offered one card for it, and the second occurrence was
// never booked. Lines saved before this ticket carry no key and collapse
// exactly as they did before.
export const getExtraOccurrenceIdentityKey = (extra) =>
  `${getExtraProductKey(extra)}-${extra?.occurrenceKey ?? ''}`;

// FR-2 — the travellers an occurrence hands over when it is halved, once no
// unassigned traveller is left to serve the new one.
//
// Halving each side on its own loses a traveller whenever both are odd: an
// occurrence of 1 adult and 1 child gave nobody away, so the split was refused
// on a party that splits perfectly well into two vehicles of one, and {3, 3}
// moved only 2 and left 4 facing 2. The half is counted on the PARTY, then
// taken from whichever side still has someone to give — adults first, so the
// new occurrence is not left children-only while adults remain to move.
//
// The shortfall is at most one traveller, which is what the two floors can lose
// between them, so a single top-up always closes it. Returns zeroes for a party
// of one, the only case that genuinely cannot be halved.
export const getHalvedOccurrencePax = ({ adults = 0, children = 0 } = {}) => {
  const half = Math.floor((adults + children) / 2);

  let movedAdults = Math.floor(adults / 2);
  let movedChildren = Math.floor(children / 2);
  let shortfall = half - (movedAdults + movedChildren);

  if (shortfall > 0) {
    const topUpAdults = Math.min(shortfall, adults - movedAdults);
    movedAdults += topUpAdults;
    shortfall -= topUpAdults;
    movedChildren += Math.min(shortfall, children - movedChildren);
  }

  return { adults: movedAdults, children: movedChildren };
};

// KD-3650 — the party ONE occurrence is retained for, read as a whole.
//
// A line that owns a pax count owns its children too: `adults`/`adultCount` is
// what says it owns one, and its children go with it. Neither a booking nor a
// saved trip records an empty `childrenBirthdates`, so an occurrence retained
// for adults only comes back with no children field at all — falling back one
// field at a time then handed it every child of the trip, and built a party
// nobody ever selected.
//
// Only a line carrying no pax count at all — everything saved before this
// ticket — falls back on the trip composition, exactly as it did before.
export const getExtraOccurrencePax = (extra, fallbackComposition) => {
  const ownAdults = extra?.adults ?? extra?.adultCount;

  if (ownAdults == null) {
    return {
      adults: fallbackComposition?.adults ?? 0,
      childrenBirthdates: fallbackComposition?.childrenBirthdates ?? [],
    };
  }

  return {
    adults: ownAdults,
    childrenBirthdates: extra?.childrenBirthdates ?? [],
  };
};

// What one selected line costs the trip: `extra.price` is a unit price, so a
// per-pax option is counted once per traveller of its occurrence, while a flat
// one is charged once whatever the party.
export const getExtraAmount = (extra) => {
  const unitPrice = Number.parseFloat(extra?.price) || 0;

  if (!extra?.perPax) {
    return unitPrice;
  }

  const { adults, childrenBirthdates } = getExtraOccurrencePax(extra);

  return unitPrice * (adults + childrenBirthdates.length);
};

// KD-3744 — the per-traveller share of one selected line: its amount divided by
// the travellers retained for it, whatever its pricing unit. A flat-fee service
// is split too, so the Trip Designer reads the same way for every product.
// A line retained for nobody has no share to state.
export const getExtraAmountPerPax = (extra) => {
  const { adults, childrenBirthdates } = getExtraOccurrencePax(extra);
  const paxCount = adults + childrenBirthdates.length;

  return paxCount > 0 ? getExtraAmount(extra) / paxCount : null;
};

// KD-3744 — the per-traveller share of a set of lines. The occurrences of one
// service are read together: its amount is divided by the travellers retained
// across all of them, so a service split 2 + 1 is shared between 3, whatever
// the size of the trip. Each service then adds its own share to the others —
// summing the pax of different services would count a traveller once per
// service. A set retained for nobody has no share to state.
export const sumExtrasAmountPerPax = (extras) => {
  const services = new Map();

  (extras ?? []).forEach((extra) => {
    const key = `${extra?.id}:${extra?.parentExtraId ?? ''}`;
    const { adults, childrenBirthdates } = getExtraOccurrencePax(extra);
    const service = services.get(key) ?? { amount: 0, paxCount: 0 };

    services.set(key, {
      amount: service.amount + getExtraAmount(extra),
      paxCount: service.paxCount + adults + childrenBirthdates.length,
    });
  });

  const shares = [...services.values()]
    .filter((service) => service.paxCount > 0)
    .map((service) => service.amount / service.paxCount);

  return shares.length
    ? shares.reduce((total, share) => total + share, 0)
    : null;
};

// AC9/AC10 — whether splitting is offered on a service, i.e. whether its price
// grid has a variable per-person price: two per-pax tiers whose unit price
// differs. A grid that charges the same per person at every pax count carries
// no pricing meaning to split, so the link is not offered on it.
//
// Only per-pax tiers are read. A flat-fee tier prices the party, not the
// person, and its unit price is merely the fee divided by the party — it varies
// with every composition without the grid being variable at all.
//
// Cents are enough here, this only decides whether the link shows up.
export const isExtraSplittable = (pricings) =>
  new Set(
    (pricings ?? [])
      .filter((pricing) => pricing?.perPax === true)
      .map((pricing) => {
        const pax =
          (pricing?.validForAdultCount ?? 0) +
          (pricing?.validForChildrenBirthdates?.length ?? 0);
        if (!pax || pricing?.price == null) return null;
        return Math.round((pricing.price / pax) * 100);
      })
      .filter((unitPrice) => unitPrice !== null),
  ).size >= 2;

// KD-3650 / FR-4 — whether a selected line is THE occurrence a removal cross
// was clicked on, rather than merely another occurrence of the same service.
//
// The timeline chips are occurrences, but the predicate behind their cross
// matched on `id` alone: it designated the whole service, so removing one chip
// took its siblings down with it. `occurrenceKey` is the only field that tells
// two occurrences apart.
//
// Either side missing a key is a line saved before this ticket — the key is
// nullable and never backfilled — and nothing is left to tell the occurrences
// apart, so the service-level match stands, exactly as it did before.
export const isSameExtraOccurrence = (extra, target) =>
  extra?.occurrenceKey == null ||
  target?.occurrenceKey == null ||
  extra.occurrenceKey === target.occurrenceKey;

// KD-3650 — the chips of one service, in the order the modal numbers them.
//
// `rank` is the order the occurrences were created in, and it is what the panel
// shows as Nº 1, Nº 2… The selection array is in whatever order the API
// returned and the hydration appended, which put Nº 2 before Nº 1 on reload
// while the modal — which sorts — kept them straight.
//
// Only lines of the SAME product are reordered: each product keeps the place
// its first line already holds, so the band's overall order is untouched and
// two occurrences merely end up adjacent. Lines carrying no rank sort as 0 and,
// the sort being stable, keep the order they came in.
export const sortExtraOccurrences = (extras) => {
  const list = Array.isArray(extras) ? extras : [];
  const productOrder = new Map();

  list.forEach((extra) => {
    const key = getExtraProductKey(extra);
    if (!productOrder.has(key)) {
      productOrder.set(key, productOrder.size);
    }
  });

  return [...list].sort((a, b) => {
    const groupDelta =
      productOrder.get(getExtraProductKey(a)) -
      productOrder.get(getExtraProductKey(b));

    if (groupDelta !== 0) {
      return groupDelta;
    }

    return (a?.rank ?? 0) - (b?.rank ?? 0);
  });
};

export const sortExtras = (extras) => {
  return extras.sort((a, b) => {
    if (a.scope === ExtraScopeEnum.GLOBAL && b.scope !== ExtraScopeEnum.GLOBAL)
      return -1;
    if (a.scope !== ExtraScopeEnum.GLOBAL && b.scope === ExtraScopeEnum.GLOBAL)
      return 1;

    if (a.dayIndex !== b.dayIndex) {
      return a.dayIndex - b.dayIndex;
    }

    if (a?.isIncluded && !b?.isIncluded) return -1;
    if (!a?.isIncluded && b?.isIncluded) return 1;

    // KD-3650 — two occurrences of the same service are alike on every key
    // above, and the order they come back in is whatever the API happened to
    // return: `rank` is the order they were created in, and the numbering the
    // panel shows has to follow it.
    //
    // Only ever applied when BOTH entries carry a rank. Lines saved before this
    // ticket have none, and comparing them against a ranked one would move them
    // relative to entries this sort has already declared equal; left to the
    // stable sort, they keep the order they came in.
    if (a?.rank != null && b?.rank != null && a.rank !== b.rank) {
      return a.rank - b.rank;
    }

    return 0;
  });
};

/**
 * KD-3581 §5 — the date, or the date range, an additional service applies on.
 *
 * Three cases, and they are decided by the SCOPE first:
 *  - a day service applies on its own day, always a single date;
 *  - a global service applies over the whole experience, so it reads the frame:
 *    a range on a multi-day frame, a single date on a one-day frame;
 *  - a global service carrying its own execution date — a transfer, typically —
 *    is shown on that date, which takes precedence over the range;
 *  - a global transfer filed on the first day (its default slot) runs on the
 *    LAST day of the experience, whatever its duration: one start date, no
 *    range. `entries` is the experience's catalogue (`experience.extras`), read
 *    like the end-location logic does to tell a transfer apart.
 *
 * The third case is read off `dayIndex`, the only date signal a selected extra
 * carries (no API field exists for it, decision Q1). But `dayIndex` is also
 * where a global extra is merely FILED: the composer stores every global option
 * on the first day of its experience. So the filing slot and a real execution
 * date look identical on the first day and only there — a global extra sitting
 * on `frame.startDayIndex` is read as filed, i.e. as applying to the whole
 * frame, and one sitting on any other day is read as carrying its own date.
 *
 * Indices are 0-based session indices, so a date is `departureDate + index`
 * days, exactly as the composer already computes each day's header date.
 *
 * `to` always carries a date, equal to `from` on a single one: a caller
 * rendering "from → to" never has to guard against a missing bound, and `kind`
 * stays the only thing to branch on. Returns `null` when the trip has no usable
 * departure date — there is no date to show at all then, and showing an invalid
 * one would be worse.
 */
export const getServiceApplicationDate = ({
  departureDate,
  extra,
  frame,
  entries,
}) => {
  const departure = dayjs(departureDate);

  if (!departureDate || !departure.isValid()) {
    return null;
  }

  const dateOfDay = (dayIndex) =>
    departure.clone().add(dayIndex, 'days').toDate();

  const single = (dayIndex) => {
    const date = dateOfDay(dayIndex);
    return {
      kind: ServiceApplicationDateKindEnum.SINGLE,
      from: date,
      to: date,
    };
  };

  const frameStartDayIndex = Number.isInteger(frame?.startDayIndex)
    ? frame.startDayIndex
    : null;
  const frameEndDayIndex = Number.isInteger(frame?.endDayIndex)
    ? frame.endDayIndex
    : null;
  const extraDayIndex = Number.isInteger(extra?.dayIndex)
    ? extra.dayIndex
    : null;

  // A day service — and anything whose scope is unreadable, which is displayed
  // on the day it is attached to rather than spread over the experience.
  if (extra?.scope !== ExtraScopeEnum.GLOBAL) {
    return single(extraDayIndex ?? frameStartDayIndex ?? 0);
  }

  // A global service filed on a day of its own: that date wins over the range.
  if (
    extraDayIndex != null &&
    frameStartDayIndex != null &&
    extraDayIndex !== frameStartDayIndex
  ) {
    return single(extraDayIndex);
  }

  const from = frameStartDayIndex ?? extraDayIndex ?? 0;
  const to = frameEndDayIndex ?? from;

  if (isTransferTypology(extra, entries ?? [])) {
    return single(to);
  }

  // A one-day experience reads as a date, not as a range of one day.
  if (to <= from) {
    return single(from);
  }

  return {
    kind: ServiceApplicationDateKindEnum.RANGE,
    from: dateOfDay(from),
    to: dateOfDay(to),
  };
};

// The pricing line of an extra that matches a party exactly: same adult count,
// same children (compared by birth date).
export function getPricingForCompositionExtra({ composition, pricings = [] }) {
  const adults = composition?.adults ?? composition?.adultCount ?? 0;
  const childrenBirthdates = composition?.childrenBirthdates ?? [];
  const formattedChildrenBirthdates = childrenBirthdates.map((birthdate) =>
    toDateString(birthdate),
  );

  return pricings?.find((price) => {
    return (
      price.validForAdultCount === adults &&
      price.validForChildrenBirthdates.length === childrenBirthdates.length &&
      price.validForChildrenBirthdates.every((birthdate) =>
        formattedChildrenBirthdates.includes(toDateString(birthdate)),
      )
    );
  });
}
